import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography, IconButton } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DeleteIcon from '@mui/icons-material/Delete';

import { audioBoxStyles, fileInfoStyles, uploadButtonStyles } from './styles';

interface FileUploaderProps {
  audioFile: File | null;
  isUploading: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: () => void;
}

export const FileUploader = ({
  audioFile,
  isUploading,
  fileInputRef,
  onFileUpload,
  onDeleteFile,
}: FileUploaderProps) => {
  const { t } = useTranslation();
  if (isUploading) {
    return (
      <Stack gap={2}>
        <Typography variant="subtitle2">{t('callsMenu.selectedStage.file.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            {t('callsMenu.selectedStage.file.uploading')}
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (audioFile) {
    return (
      <Stack gap={2}>
        <Typography variant="subtitle2">{t('callsMenu.selectedStage.file.title')}</Typography>
        <Box sx={audioBoxStyles}>
          <Box sx={fileInfoStyles}>
            <AudioFileIcon sx={{ color: (theme) => theme.palette.primary.main }} />
            <Typography variant="body2" color="text.primary">
              {audioFile.name || t('callsMenu.selectedStage.file.uploaded')}
            </Typography>
          </Box>
          <IconButton
            aria-label={t('callsMenu.selectedStage.file.delete')}
            sx={{ color: (theme) => theme.palette.action.disabled }}
            onClick={onDeleteFile}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">{t('callsMenu.selectedStage.file.title')}</Typography>
      <Stack gap={2}>
        <Button variant="outlined" startIcon={<FileUploadIcon />} component="label" sx={uploadButtonStyles}>
          {t('callsMenu.selectedStage.file.upload')}
          <input type="file" hidden ref={fileInputRef} accept="audio/*" onChange={onFileUpload} className="nodrag" />
        </Button>
      </Stack>
    </Stack>
  );
};
